'use client'

import Menu from '@component/menu'
import { assign, find, get, isEqual, isFunction, map, replace, size } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useFilterBaseContext, { FilterBaseContext, useFilterBaseState } from './conetxt'
import classNames from 'classnames'
import { queryToStr } from '@plugin/searchRoute/utils'
import useFetch from '@hooks/useFetch'
import { AGENT_FILTER } from '@homeSearch/modules/filter/config'
import MaskLoading from '@component/loading/mask'
import { stopPropagation } from '@utils'

export function FilterBase({
  data,
  children,
  params,
  onConfirm,
  confirmLabel,
  className
}) {

  const state = useFilterBaseState(params)
  const { resetFilter, defaultFilter } = state
  const isActive = useMemo(() => size(get(defaultFilter, data.alias)) > 0, [defaultFilter, data])
  return (
    <FilterBaseContext.Provider value={{ ...state, confirmLabel }}>
      <Menu
        className={className}
        left
        hover={false}
        onClose={resetFilter}
        popup={<FilterPopup data={data} onConfirm={onConfirm}></FilterPopup>}
        width={375}
        contentClassName='rounded-md'
        fixedBg
      >
        <div className={classNames(
          'border h-40 rounded-2xl text-font inline-flex items-center px-20 cursor-pointer',
          { 'border-primary bg-primary-light !text-primary': isActive }
        )}>
          <span className='text-sm whitespace-nowrap'>{children}</span>
          <i className='icon icon-arrow-down !text-xs ml-4'></i>
        </div>
      </Menu>
    </FilterBaseContext.Provider>
  )
}

function FilterPopup({
  data,
  itemClick,
  onConfirm
}) {

  // 单个filter的参数
  const { filter, defaultParams, resetFilter, confirmLabel } = useFilterBaseContext()
  // 当前显示的数据
  const [viewData, setViewData] = useState(data)
  // 每个filter独自获取数据
  const params = useMemo(() => {
    return assign({}, defaultParams, { filter: queryToStr(filter) })
  }, [filter, defaultParams])
  const { data: filterData, isLoading } = useFetch({ url: AGENT_FILTER.url, params })
  // 选中的数量
  const [totals, setTotals] = useState(0)
  // confirm button label
  const confirmText = useMemo(() => replace(confirmLabel, '{totals}', totals), [totals, confirmLabel])
  useEffect(() => {
    const n = get(filterData, 'headers.x-selected-count', 0) || get(filterData, 'headers.x-total-count', 0)
    if (n) setTotals(n)
  }, [filterData, setTotals])
  useEffect(() => {
    if (size(filterData)) {
      const d = find(filterData, item => item.alias === data.alias)
      if (d) setViewData(d)
    }
  }, [data, filterData, setViewData])

  // confirm
  const confirmHandle = useCallback(() => {
    isFunction(onConfirm) && onConfirm(filter)
  }, [onConfirm, filter])
  // clear
  const clearHandle = useCallback(event => {
    event.stopPropagation()
    resetFilter(viewData.alias)
  }, [resetFilter, viewData])

  return (
    <div className='py-20 relative'>
      <div onClick={stopPropagation}>
        <MaskLoading transparent loading={isLoading}></MaskLoading>
        <div className='text-font text-base px-20 font-medium'>{viewData.label}</div>
        <div className='px-20 mt-10 mb-14 grid grid-cols-2 gap-10'>
          {
            map(viewData.children, item => (
              <ItemBlock key={item.alias + item.value} data={item} onClick={itemClick}></ItemBlock>
            ))
          }
        </div>
      </div>
      <div className='grid grid-cols-[1fr,2fr] px-20 gap-x-10'>
        <button className='button-primary-out' onClick={clearHandle}>Clear</button>
        <button className='button-primary' onClick={confirmHandle}>{confirmText}</button>
      </div>
    </div>
  )
}

function ItemBlock({
  data
}) {

  const { filter, toggleFilter } = useFilterBaseContext()
  const isActive = useMemo(() => {
    let list = get(filter, data.alias, [])
    return list.includes(parseFloat(data.value))
  }, [filter, data])
  const clickHandle = useCallback(event => {
    event.stopPropagation()
    toggleFilter(data.alias, data.value)
  }, [data, toggleFilter])

  return (
    <div
      className={classNames(
        'flex items-center justify-between h-50 border rounded-md px-10 text-sm cursor-pointer',
        { 'border-primary bg-primary-light': isActive },
        { 'cursor-not-allowed pointer-events-none': !isActive && !data.num }
      )}
      onClick={clickHandle}
    >
      <span className={isActive ? 'text-primary' : 'text-neutral-5'}>{data.label}</span>
      <span className={isActive ? 'text-primary' : 'text-neutral-3'}>({data.num})</span>
    </div>
  )
}


export function SortBy({
  label = 'Sort by:',
  value,
  data,
  onChange,
  className
}) {

  const [val, setVal] = useState(value)
  const clickHandle = useCallback(item => {

    return () => {
      if (isEqual(val, item)) return
      setVal(item)
      isFunction(onChange) && onChange(item)
    }
  }, [onChange, setVal, val])

  const popup = useMemo(() => {
    return (
      <div className='p-16'>
        <div className='text-font text-base font-medium'>{label}</div>
        <div className='mt-10 space-y-10'>
          {map(data, item => (
            <div
              key={item.label}
              className={classNames(
                'px-10 h-50 flex items-center border rounded-sm text-neutral-5 text-sm cursor-pointer',
                { 'border-primary bg-primary-light !text-primary': isEqual(val, item) }
              )}
              onClick={clickHandle(item)}
            >{item.label}</div>
          ))}
        </div>
      </div>
    )
  }, [data, val, label, clickHandle])

  return (
    <Menu
      hover={false}
      right
      popup={popup}
      width={375}
      contentClassName='rounded-md'
      className={className}
    >
      <div className='border h-40 rounded-2xl text-font inline-flex items-center px-20 cursor-pointer'>
        <div className='text-sm whitespace-nowrap'>
          {label}
          <span className='text-primary ml-4'>{get(val, 'label')}</span>
        </div>
      </div>
    </Menu>
  )
}